/**
 * API клиент для работы с backend
 * Сохраняет совместимость с существующим PHP API
 */

import { useApi } from '@/composables/useApi'
import { storage } from '@/composables/useStorage'

export class ApiClient {
  constructor() {
    const api = useApi()
    this.get = api.get
    this.post = api.post
    this.put = api.put
    this.delete = api.delete
  }

  // Получить выбранный город (из пользователя или localStorage)
  getSelectedCity() {
    try {
      const savedUser = storage.get('auth_user')
      if (savedUser?.city) {
        return savedUser.city
      }
      const savedCity = storage.get('selected_city')
      return savedCity || null
    } catch (e) {
      return null
    }
  }

  // Добавить city к query параметрам если нужно
  addCityToParams(params = {}) {
    const city = this.getSelectedCity()
    if (city) {
      return { ...params, city }
    }
    return params
  }

  // ========== Auth API ==========
  async authTelegram(initData) {
    return this.post('auth/telegram', {
      initData: initData
    })
  }

  async authPhone(phone, code) {
    return this.post('auth/phone', {
      phone: phone,
      code: code
    })
  }

  async sendPhoneCode(phone) {
    // Код входа приходит в Telegram: без него войти в существующий аккаунт нельзя.
    return this.post('auth/phone/request', {
      phone: phone
    })
  }

  async checkAuth() {
    return this.post('auth/check')
  }

  // ========== Menu API ==========
  async getMenu() {
    const params = this.addCityToParams()
    return this.get('menu/all', params)
  }

  async getCategories() {
    const params = this.addCityToParams()
    return this.get('menu/categories', params)
  }

  async getStopList() {
    // В новом API стоп-лист включен в меню
    const menu = await this.getMenu()
    return { stop_list: menu?.stop_list || [] }
  }

  // ========== Banners API ==========
  async getBanners() {
    const params = this.addCityToParams()
    return this.get('banners', params)
  }

  // ========== Cart API ==========
  async getCart() {
    return this.get('cart')
  }

  async addToCart(
    productId,
    quantity,
    modifiers = [],
    selectedModifiers = [],
    productData = {},
  ) {
    return this.post('cart/item', {
      product_id: productId,
      quantity: quantity,
      modifiers: modifiers,
      selected_modifiers: selectedModifiers,
      name: productData.name,
      price: productData.price
    })
  }

  async updateCartItem(itemIndex, quantity) {
    return this.put(`cart/item/${itemIndex}`, {
      quantity: quantity
    })
  }

  async removeFromCart(itemIndex) {
    return this.delete(`cart/item/${itemIndex}`)
  }

  async clearCart() {
    return this.delete('cart')
  }

  // ========== Order API ==========
  async getOrders() {
    return this.get('order')
  }

  async getOrder(orderId) {
    return this.get(`order/${orderId}`)
  }

  async createOrder(orderData) {
    // Витрина одна для сайта и Telegram Mini App. Источник фиксируем при
    // создании заказа, чтобы общая аналитика не смешивала каналы.
    const isTelegramMiniApp = Boolean(
      typeof window !== 'undefined' && window.Telegram?.WebApp?.initData
    )
    return this.post('order', {
      ...orderData,
      order_source: isTelegramMiniApp ? 'telegram_mini_app' : 'web'
    })
  }

  // ========== Promo API ==========
  async applyPromo(code) {
    return this.post('cart/promo', {
      code: code
    })
  }

  async removePromo() {
    return this.delete('cart/promo')
  }

  // ========== Favorites API ==========
  async getFavorites() {
    return this.get('favorites')
  }

  async addFavorite(productId) {
    return this.post('favorites/add', {
      product_id: productId
    })
  }

  async removeFavorite(productId) {
    return this.delete('favorites/remove', {
      product_id: productId
    })
  }

  async checkFavorite(productId) {
    return this.get('favorites/check', {
      product_id: productId
    })
  }

  // ========== User API ==========
  async updateCity(city) {
    return this.put('user/profile', {
      city: city
    })
  }

  async updatePhone(phone) {
    return this.put('user/profile', {
      phone: phone
    })
  }

  // ========== Settings API ==========
  async getCities() {
    return this.get('admin/settings/cities')
  }

  // ========== Pages API ==========
  async getPageBySlug(slug) {
    const params = this.addCityToParams()
    return this.get(`pages/by-slug/${slug}`, params)
  }

  async getPagesByCity(city) {
    return this.get(`pages/by-city/${city}`)
  }
}

// Экспортируем singleton instance
export const apiClient = new ApiClient()

